import { apiGet, apiPost, apiPatch } from "./client";
import type { Pipeline, PipelineStage, PipelineAction, Opportunity, Company } from "@sales-ops-amplify/shared";

/** Simple hash function matching the backend's simpleHash for URL dedup. */
export function simpleHash(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }
  return Math.abs(hash).toString(16);
}

// ---- Pipeline-specific endpoints (stages, actions, workflow) ----

export interface PipelineDetail {
  pipeline: Pipeline;
  stages: PipelineStage[];
  actions: PipelineAction[];
  opportunity?: Opportunity;
  /** Wire alias kept for older API responses. Prefer `opportunity`. */
  job?: Opportunity;
  company?: Company;
}

export interface ListPipelinesResponse {
  pipelines: Pipeline[];
  total: number;
}

/** Create a pipeline for an opportunity. */
export async function createPipeline(opportunityId: string): Promise<{ pipeline: Pipeline }> {
  return apiPost(`/api/pipeline/for-opportunity/${opportunityId}`);
}

/** List all pipelines. */
export async function listPipelines(): Promise<ListPipelinesResponse> {
  return apiGet<ListPipelinesResponse>("/api/pipeline/list");
}

/** Get full pipeline detail (stages + actions + linked job + company). */
export async function getPipelineDetail(pipelineId: string): Promise<PipelineDetail> {
  const res = await apiGet<PipelineDetail>(`/api/pipeline/detail/${pipelineId}`);
  return {
    ...res,
    opportunity: res.opportunity ?? res.job,
  };
}

/** Update pipeline status. */
export async function updatePipelineStatus(pipelineId: string, status: string): Promise<void> {
  await apiPatch(`/api/pipeline/detail/${pipelineId}`, { status });
}

/** Advance pipeline to a new stage. */
export async function advanceStage(pipelineId: string, stageName: string): Promise<void> {
  await apiPatch(`/api/pipeline/detail/${pipelineId}/stage`, { stageName });
}

export interface PipelineRunResult {
  success: boolean;
  output?: string;
  score?: number;
  error?: string;
  action?: PipelineAction;
}

/** Run a prompt template for a pipeline stage. */
export async function runPipelinePrompt(
  pipelineId: string,
  input: { promptKind: string; stageName?: string },
): Promise<PipelineRunResult> {
  return apiPost<PipelineRunResult>(`/api/pipeline/detail/${pipelineId}/run`, input);
}

/** Record an action on a pipeline. */
export async function recordAction(
  pipelineId: string,
  action: { type: string; status?: string; result?: string; metadata?: Record<string, unknown>; stageId?: string },
): Promise<{ action: PipelineAction }> {
  return apiPost(`/api/pipeline/detail/${pipelineId}/actions`, action);
}

/** List actions for a pipeline. */
export async function listActions(pipelineId: string): Promise<{ actions: PipelineAction[]; total: number }> {
  return apiGet(`/api/pipeline/detail/${pipelineId}/actions`);
}